import PlayerService from '../../services/PlayerService';
import { SeededRandom } from './SeededRandom';

// Constants from ProbabilityUtils
const PLANCK_CONSTANT = 6.62607015e-34;
const BOLTZMANN_CONSTANT = 1.380649e-23;
const TEMPERATURE = 300; // Room temperature in Kelvin
const MIN_FREQ = 1e-6;    // 1 µHz (micro-hertz) - Covers extremely low frequency waves
const MAX_FREQ = 1e28;    // 10²⁸ Hz - Covers highest energy gamma rays and beyond

class QuantumEngine {
  constructor(seed = undefined) {
    if (QuantumEngine.instance) {
      return QuantumEngine.instance;
    }
    QuantumEngine.instance = this;

    // Generate a new random initial seed each time (human-readable format)
    const generateRandomSeed = () => {
      // Use crypto for true randomness
      const getRandomInt = (min, max) => {
        const range = max - min + 1;
        const bytes = new Uint32Array(1);
        crypto.getRandomValues(bytes);
        return min + (bytes[0] % range);
      };

      // Generate four parts for the seed:
      // 1. Universe Cluster (AA): Alpha-numeric (36^2 possibilities)
      // 2. Timeline (TTTT): 0000-9999
      // 3. Universe (UUUU): 0000-9999
      // 4. Reality (RRRR): 0000-9999
      
      // Generate alpha-numeric cluster ID (36^2 possibilities = 1,296 clusters)
      const alphabet = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ';
      const clusterPart = Array(2).fill(0)
        .map(() => alphabet[getRandomInt(0, alphabet.length - 1)])
        .join('');
      
      const timelinePart = getRandomInt(0, 9999).toString().padStart(4, '0');
      const universePart = getRandomInt(0, 9999).toString().padStart(4, '0');
      const realityPart = getRandomInt(0, 9999).toString().padStart(4, '0');

      // Format as AA-TTTT-UUUU-RRRR (e.g., X7-4221-8832-1924)
      const formattedSeed = `${clusterPart}-${timelinePart}-${universePart}-${realityPart}`;
      
      // For internal use, convert to number but preserve the format for display
      this.formattedSeed = formattedSeed;
      
      // Convert cluster to number (base 36 to base 10)
      const clusterNum = parseInt(clusterPart, 36);
      
      // Combine parts into a single number for actual seed
      return parseInt(`${clusterNum}${timelinePart}${universePart}${realityPart}`);
    };

    // Always generate a new initial seed
    this.initialSeed = generateRandomSeed();
    
    // Use provided seed or initial seed for master seed
    this.masterSeed = seed || this.initialSeed;
    this.rng = new SeededRandom(this.masterSeed);
    this.baseTime = Date.now();

    // Configuration for reality metrics
    this.config = {
      // Base probabilities for reality plane events
      eventProbabilities: {
        anomaly: 0.05,    // Reduced from 0.15 - Much rarer quantum anomalies
        shift: 0.08,      // Reduced from 0.25 - Much rarer reality shifts
        merge: 0.03,      // Reduced from 0.10 - Much rarer reality merges
        split: 0.06       // Reduced from 0.20 - Much rarer reality splits
      },
      // Metrics ranges for reality planes
      metrics: {
        stability: {
          min: 0.05,
          max: 2.0
        },
        entropy: {
          min: 0.1,
          max: 3.0
        },
        complexity: {
          min: 0.2,
          max: 4.0
        },
        coherence: {
          min: 0.1,
          max: 2.5
        }
      }
    };

    // Initialize with base frequency and qubit count
    this.currentState = {
      frequency: this.generateRandomFrequency(),
      harvestedQubits: 0
    };
    
    // Generate full initial state
    this.currentState = {
      ...this.generateQuantumMetrics(),
      ...this.generateSignalMetrics(),
      frequency: this.currentState.frequency,
      harvestedQubits: 0
    };
    
    // Subscribers for quantum data updates
    this.subscribers = new Set();
    
    // Update interval in milliseconds
    this.updateInterval = 1000;
    
    // Frame counter for quantum effects
    this.frameCount = 0;

    // Scanning state with enhanced motion control
    this.scanningState = {
      isScanning: true,
      sweepSpeed: 0.005, // Reduced for smoother motion
      momentum: 0,
      lastSweepPos: 0,
      detectedFrequency: null,
      signalLockStrength: 0,
      smoothingFactor: 0.15 // Controls transition smoothness
    };
    
    // Start the quantum simulation
    this.startSimulation();
  }

  // Get seeded timestamp
  getSeededTime() {
    return this.rng.getTimestamp(this.baseTime, this.frameCount * this.updateInterval);
  }

  // Reset to specific frame with same seed
  resetToFrame(frame) {
    this.rng = new SeededRandom(this.initialSeed);
    this.frameCount = frame;
    
    // Regenerate state for this frame
    this.currentState = {
      ...this.generateQuantumMetrics(),
      ...this.generateSignalMetrics(),
      frequency: this.generateRandomFrequency(),
      harvestedQubits: 0
    };
    
    this.notifySubscribers();
  }

  // Get all possible states for a range of frames
  getAllStatesForFrameRange(startFrame, endFrame) {
    const states = [];
    const originalFrame = this.frameCount;
    
    for (let frame = startFrame; frame <= endFrame; frame++) {
      this.resetToFrame(frame);
      states.push({
        frame,
        ...this.getCurrentState()
      });
    }
    
    // Restore original state
    this.resetToFrame(originalFrame);
    return states;
  }

  // Get the current scanning frequency based on sweep with enhanced spectrum coverage
  getScanningFrequency() {
    if (!this.scanningState.isScanning) {
      return this.scanningState.detectedFrequency;
    }

    // Use multiple sine waves for complex motion across spectrum bands
    const t = this.frameCount * this.scanningState.sweepSpeed;
    
    // Define frequency bands (in Hz)
    const bands = [
      { name: 'ELF', min: 1e-6, max: 1e0 },    // Extremely Low Frequency
      { name: 'RF', min: 1e0, max: 1e12 },     // Radio Frequency
      { name: 'THz', min: 1e12, max: 1e14 },   // Terahertz
      { name: 'IR', min: 1e14, max: 1e15 },    // Infrared
      { name: 'Visible', min: 1e15, max: 1e15 * 0.789 }, // Visible Light
      { name: 'UV', min: 1e15 * 0.789, max: 1e17 }, // Ultraviolet
      { name: 'X-Ray', min: 1e17, max: 1e20 }, // X-Rays
      { name: 'Gamma', min: 1e20, max: 1e28 }  // Gamma Rays
    ];
    
    // Multi-wave scanning pattern
    const primaryWave = Math.sin(t);
    const secondaryWave = Math.sin(t * 0.3) * 0.3;
    const tertiaryWave = Math.sin(t * 0.1) * 0.1;
    const microWave = Math.sin(t * 3) * 0.05;
    
    // Combine waves with enhanced resolution
    let combinedWave = (primaryWave + secondaryWave + tertiaryWave + microWave + 1) * 0.5;
    
    // Apply non-linear easing for better band resolution
    combinedWave = this.easeInOutSine(combinedWave);
    
    // Select frequency band based on sweep position
    const bandIndex = Math.floor(combinedWave * bands.length);
    const band = bands[Math.min(bandIndex, bands.length - 1)];
    
    // Calculate in-band position
    const bandPosition = (combinedWave * bands.length) % 1;
    
    // Apply logarithmic scaling within the band
    const logMin = Math.log(band.min);
    const logMax = Math.log(band.max);
    const logSweep = logMin + (this.easeInOutSine(bandPosition) * (logMax - logMin));
    const frequency = Math.exp(logSweep);
    
    // Add quantum jitter for realistic scanning
    const jitterScale = Math.log(frequency) / Math.log(MAX_FREQ);
    const quantumJitter = (this.rng.random() - 0.5) * 0.001 * (1 + jitterScale);
    
    // Update current state with scanning frequency
    this.currentState.frequency = frequency * (1 + quantumJitter);
    
    return this.currentState.frequency;
  }

  // Smooth easing function
  easeInOutSine(x) {
    return -(Math.cos(Math.PI * x) - 1) / 2;
  }

  // Enhanced signal detection with spectrum-aware characteristics
  detectSignal() {
    if (!this.scanningState.isScanning) return false;
    
    // Get current scanning frequency
    const currentScanFreq = this.getScanningFrequency();
    
    // Define frequency band characteristics
    const bandCharacteristics = [
      { min: 1e-6, max: 1e0, name: 'ELF', baseProb: 0.008, atmosphericNoise: 0.3 },
      { min: 1e0, max: 1e12, name: 'RF', baseProb: 0.006, atmosphericNoise: 0.2 },
      { min: 1e12, max: 1e14, name: 'THz', baseProb: 0.005, atmosphericNoise: 0.15 },
      { min: 1e14, max: 1e15, name: 'IR', baseProb: 0.004, atmosphericNoise: 0.1 },
      { min: 1e15, max: 1e15 * 0.789, name: 'Visible', baseProb: 0.007, atmosphericNoise: 0.05 },
      { min: 1e15 * 0.789, max: 1e17, name: 'UV', baseProb: 0.003, atmosphericNoise: 0.08 },
      { min: 1e17, max: 1e20, name: 'X-Ray', baseProb: 0.002, atmosphericNoise: 0.02 },
      { min: 1e20, max: 1e28, name: 'Gamma', baseProb: 0.001, atmosphericNoise: 0.01 }
    ];
    
    // Find current frequency band
    const currentBand = bandCharacteristics.find(band =>
      currentScanFreq >= band.min && currentScanFreq < band.max
    ) || bandCharacteristics[bandCharacteristics.length - 1];
    
    // Calculate base detection probability
    const timeOfDay = (Date.now() % 86400000) / 86400000; // 0-1 representing time of day
    const dayNightFactor = 1 + Math.sin(timeOfDay * Math.PI * 2) * 0.2; // ±20% variation
    
    // Atmospheric conditions affect lower frequencies more
    const atmosphericEffect = Math.exp(-currentBand.atmosphericNoise * dayNightFactor);
    
    // Quantum effects are stronger at higher frequencies
    const quantumFactor = Math.log(currentScanFreq) / Math.log(MAX_FREQ);
    const quantumEffect = 1 + (quantumFactor * 0.5);
    
    // Calculate final detection probability
    const baseProbability = currentBand.baseProb * atmosphericEffect * quantumEffect;
    const tunnelFactor = Math.exp(-8 * baseProbability); // Quantum tunneling effect
    const finalProbability = baseProbability * (1 + tunnelFactor) * dayNightFactor;
    
    // Add random quantum fluctuations
    const quantumFluctuation = (this.rng.random() - 0.5) * 0.1 * quantumFactor;
    const detected = this.rng.random() < (finalProbability + quantumFluctuation);

    if (detected) {
      this.scanningState.isScanning = false;
      this.scanningState.detectedFrequency = currentScanFreq;
      this.currentState.frequency = currentScanFreq;
      
      
      // Calculate initial signal lock strength based on band characteristics
      this.scanningState.signalLockStrength = Math.min(1,
        (1 - currentBand.atmosphericNoise) *
        (1 + quantumEffect * 0.2) *
        atmosphericEffect
      );
      
      // Store detection metadata
      this.scanningState.detectionMetadata = {
        band: currentBand.name,
        atmosphericConditions: atmosphericEffect,
        quantumInfluence: quantumEffect,
        timeOfDay: timeOfDay,
        signalStrength: this.scanningState.signalLockStrength
      };
    }

    return detected;
  }

  // Reset scanning
  resetScanning() {
    // Reset scanning state with all required properties
    this.scanningState = {
      isScanning: true,
      sweepSpeed: 0.005,
      momentum: 0,
      lastSweepPos: 0,
      detectedFrequency: null,
      signalLockStrength: 0,
      smoothingFactor: 0.15
    };

    // Reset frame count for smooth start
    this.frameCount = 0;

    // Update current state with initial scanning frequency
    const initialFreq = this.getScanningFrequency();
    this.currentState = {
      ...this.currentState,
      frequency: initialFreq
    };

    // Notify subscribers of the reset
    this.notifySubscribers();
  }

  // Generate a unique seed for a specific reality plane
  generatePlaneSeed(planeIndex) {
    // Use master seed and plane index to generate a unique but deterministic seed
    const planeSeedRng = new SeededRandom(this.masterSeed);
    // Skip ahead based on plane index to get unique seed
    for (let i = 0; i <= Math.abs(planeIndex); i++) {
      planeSeedRng.random();
    }
    // Generate a more varied seed using multiple random values
    const r1 = planeSeedRng.random();
    const r2 = planeSeedRng.random();
    const r3 = planeSeedRng.random();
    
    // Combine random values to create a unique seed
    const seed = Math.floor(
      (r1 * 0xFFFF) +
      (r2 * 0xFFFF) * 0x10000 +
      (r3 * 0xFF) * 0x100000000
    );
    
    return seed;
  }

  // Generate initial state for a reality plane
  generateInitialState(planeIndex = 0) {
    const planeSeed = this.generatePlaneSeed(planeIndex);
    const planeRng = new SeededRandom(planeSeed);

    // Calculate time dilation with compounding effect
    let timeDilation = 0;
    
    if (planeIndex !== 0) {
      // Base dilation rate (smaller for more subtle effect)
      const BASE_RATE = 0.15;
      
      // Use logarithmic scaling for compounding effect
      const magnitude = Math.log(Math.abs(planeIndex) + 1) * BASE_RATE;
      
      // Apply asymmetric compounding:
      // - Positive planes (up): slightly faster time (>1.0)
      // - Negative planes (down): slightly slower time (<1.0)
      if (planeIndex > 0) {
        // Time moves faster as we go up (1.0 to ~2.0)
        timeDilation = magnitude;
      } else {
        // Time moves slower as we go down (-1.0 to ~-0.5)
        timeDilation = -magnitude * 0.5;
      }
    }
    
    // Calculate distance factor for other effects
    const distanceFactor = Math.pow(1.1, Math.abs(planeIndex) * 0.1);

    // Enhanced chaotic variations
    const chaosFactor = (
      Math.sin(planeIndex * 0.5) *
      Math.cos(planeIndex * 0.3) *
      Math.sin(planeIndex * 0.7)
    ) * 0.5; // Increased chaos

    // Generate metrics with extreme variations
    const generateMetric = (range, bias = 0) => {
      const raw = planeRng.random();
      const nonLinear = Math.pow(raw, 2); // More extreme distribution
      const baseValue = nonLinear * (range.max - range.min) + range.min;
      const withDistance = baseValue * Math.pow(distanceFactor, 1.5); // Stronger distance effect
      const withChaos = withDistance * (1 + chaosFactor * 2); // Doubled chaos effect
      const biased = withChaos + (bias * distanceFactor * 2); // Stronger bias effect
      return Math.max(range.min, Math.min(range.max, biased));
    };

    // Generate metrics with enhanced biases
    const stability = generateMetric(this.config.metrics.stability, -0.2); // Much less stable further out
    const entropy = generateMetric(this.config.metrics.entropy, 0.3);      // Much more entropy further out
    const complexity = generateMetric(this.config.metrics.complexity, 0.25); // Much more complex further out
    const coherence = generateMetric(this.config.metrics.coherence, -0.15); // Much less coherent further out

    // Calculate event probabilities with enhanced effects
    const eventProbabilities = {
      anomaly: this.config.eventProbabilities.anomaly * (1 - stability) * distanceFactor,
      shift: this.config.eventProbabilities.shift * entropy * distanceFactor,
      merge: this.config.eventProbabilities.merge * coherence * (1 / distanceFactor),
      split: this.config.eventProbabilities.split * complexity * distanceFactor
    };
    
    return {
      seed: planeSeed,
      metrics: {
        stability,
        entropy,
        complexity,
        coherence,
        timeDilation
      },
      eventProbabilities,
      planeIndex
    };
  }

  generateRandomFrequency() {
    // Use the same frequency bands as scanning
    const bands = [
      { min: 1e-6, max: 1e0 },    // Extremely Low Frequency
      { min: 1e0, max: 1e12 },     // Radio Frequency
      { min: 1e12, max: 1e14 },   // Terahertz
      { min: 1e14, max: 1e15 },    // Infrared
      { min: 1e15, max: 1e15 * 0.789 }, // Visible Light
      { min: 1e15 * 0.789, max: 1e17 }, // Ultraviolet
      { min: 1e17, max: 1e20 }, // X-Rays
      { min: 1e20, max: 1e28 }  // Gamma Rays
    ];
    
    // Select random band
    const bandIndex = Math.floor(this.rng.random() * bands.length);
    const band = bands[bandIndex];
    
    // Generate frequency within selected band using logarithmic distribution
    const logMin = Math.log(band.min);
    const logMax = Math.log(band.max);
    return Math.exp(this.rng.random() * (logMax - logMin) + logMin);
  }

  generateQuantumMetrics() {
    // Enhanced temporal dynamics
    const timePhase = this.frameCount * 0.001;
    const quantumCycle = Math.sin(timePhase) * 0.5 + 0.5;
    const quantumNoise = (this.rng.random() - 0.5) * 2;

    // Multi-layered noise simulation for richer dynamics
    const baseCoherence = this.simulatePerlinNoise(this.frameCount * 0.01) *
      (1 + Math.sin(timePhase * 2) * 0.3);
    const baseEntanglement = this.simulatePerlinNoise(this.frameCount * 0.02 + 1000) *
      (1 + Math.cos(timePhase * 1.5) * 0.4);
    const basePhase = this.simulatePerlinNoise(this.frameCount * 0.015 + 2000) *
      (1 + Math.sin(timePhase * 3) * 0.2);
    
    // Enhanced decoherence dynamics
    const decoherenceTime = 1000 * (1 + quantumCycle * 0.5);
    const decoherenceFactor = Math.exp(-this.frameCount / decoherenceTime) *
      (1 + Math.abs(quantumNoise) * 0.3);
    
    // Enhanced thermal effects with quantum fluctuations
    const thermalEnergy = BOLTZMANN_CONSTANT * TEMPERATURE *
      (1 + quantumCycle * 0.2) * (1 + Math.abs(quantumNoise) * 0.1);
    const thermalNoise = Math.sqrt(thermalEnergy) *
      (this.rng.random() * 2 - 1) * (1 + quantumCycle * 0.3);

    // Enhanced entanglement with multiple quantum effects
    const tunnelProbability = Math.exp(-this.frameCount * 0.001) *
      (1 + Math.sin(timePhase * 4) * 0.2);
    const entanglementBase = (baseEntanglement * 0.8 + 0.2) * (1 - tunnelProbability);
    const entanglement = entanglementBase *
      (1 + Math.abs(quantumNoise) * 0.4) * (1 + quantumCycle * 0.3);

    // Enhanced coherence with complex environmental interactions
    const environmentalDephasing = Math.sin(this.frameCount * 0.005) * 0.1 *
      (1 + quantumCycle * 0.4) * (1 + Math.abs(quantumNoise) * 0.2);
    const coherenceBase = (baseCoherence * 0.7 + 0.3) * decoherenceFactor;
    const coherence = Math.max(0, Math.min(1,
      coherenceBase - environmentalDephasing + (quantumNoise * 0.1)
    ));

    // Enhanced phase with multiple interference patterns
    const primaryInterference = Math.sin(this.frameCount * 0.02) *
      Math.cos(this.frameCount * 0.015);
    const secondaryInterference = Math.sin(this.frameCount * 0.03) *
      Math.cos(this.frameCount * 0.025) * 0.3;
    const interferencePattern = (primaryInterference + secondaryInterference) *
      (1 + quantumCycle * 0.2);
    
    const phase = (basePhase * Math.PI * 2 + interferencePattern) % (Math.PI * 2);

    // Generate enhanced particle and cosmic data
    const particleData = this.generateParticleData(thermalEnergy, phase);
    const cosmicData = this.generateCosmicData(this.frameCount, this.currentState.frequency);

    // Calculate quantum energy with enhanced fluctuations
    const quantumEnergy = PLANCK_CONSTANT * this.frameCount *
      (1 + quantumCycle * 0.3) + thermalNoise * (1 + Math.abs(quantumNoise) * 0.2);

    return {
      frequency: this.currentState.frequency, // Explicitly include the current frequency
      entanglement_coefficient: entanglement,
      quantum_coherence: coherence,
      quantum_phase: phase,
      quantum_energy: quantumEnergy,
      decoherence_rate: 1 - decoherenceFactor,
      interference_strength: Math.abs(interferencePattern),
      quantum_cycle: quantumCycle,
      quantum_noise: quantumNoise,
      thermal_energy: thermalEnergy,
      tunnel_probability: tunnelProbability,
      particle_data: particleData,
      cosmic_data: cosmicData
    };
  }

  generateParticleData(thermalEnergy, phase) {
    // Enhanced particle types with varying energy ranges
    const particleTypes = [
      { type: 'electron', baseEnergy: 1e-19, variance: 0.5 },
      { type: 'photon', baseEnergy: 1e-17, variance: 0.7 },
      { type: 'quark', baseEnergy: 1e-15, variance: 0.9 },
      { type: 'neutrino', baseEnergy: 1e-14, variance: 0.95 },
      { type: 'muon', baseEnergy: 1e-13, variance: 0.8 },
      { type: 'tau', baseEnergy: 1e-12, variance: 0.85 }
    ];
    
    // Dynamic energy calculation with quantum fluctuations
    const timeVariance = Math.sin(this.getSeededTime() * 0.001) * 0.5 + 0.5;
    const quantumNoise = (this.rng.random() - 0.5) * 2;
    
    // Select particle type with improved energy-based probability
    const energyFactor = Math.log10(Math.max(thermalEnergy, 1e-20)) + 20; // Prevent negative log, normalize to 0-10 range
    const normalizedEnergy = Math.max(0, Math.min(1, energyFactor / 10)); // Ensure 0-1 range
    
    // Calculate weighted index with bounds protection
    const rawIndex = normalizedEnergy * (particleTypes.length - 1) * (1 + timeVariance * 0.3 + quantumNoise * 0.2);
    const typeIndex = Math.max(0, Math.min(Math.floor(rawIndex), particleTypes.length - 1));
    
    // Safely select particle type
    const selectedParticle = particleTypes[typeIndex] || particleTypes[0]; // Fallback to first particle if index is invalid
    
    // Calculate actual energy with safety checks
    const baseEnergy = selectedParticle.baseEnergy || 1e-19; // Fallback to electron energy
    const variance = selectedParticle.variance || 0.5;    // Fallback to moderate variance
    
    const actualEnergy = baseEnergy *
      (1 + (this.rng.random() - 0.5) * variance) *
      (1 + timeVariance * 0.5);

    // Enhanced spin states with protected index calculation
    const spinStates = ['spin-up', 'spin-down', 'superposition', 'exotic'];
    const normalizedPhase = (Math.sin(phase) + 1) / 2; // Ensure 0-1 range
    const spinIndex = Math.floor(
      normalizedPhase * (spinStates.length - 0.001) // Subtract small amount to prevent index == length
    );
    
    const spinState = spinStates[spinIndex] || spinStates[0];
    const spinProbability = Math.abs(Math.cos(phase + timeVariance));

    // Dynamic decay properties with safety checks
    const baseHalfLife = Math.max(actualEnergy * 1e15, 1e-15); // Prevent zero/negative half-life
    const halfLife = baseHalfLife * (1 + variance * quantumNoise);
    const decayConstant = Math.log(2) / Math.max(halfLife, 1e-20); // Prevent division by zero

    // Enhanced tunneling calculations with bounds protection
    const barrierHeight = Math.max(actualEnergy * (2 + timeVariance), 1e-20);
    const tunnelProb = Math.exp(-Math.sqrt(2 * barrierHeight)) *
      (1 + Math.abs(quantumNoise) * 0.5);

    // Ensure all numerical values are properly bounded and formatted
    return {
      particleType: {
        type: selectedParticle.type || 'electron', // Fallback to most common particle
        energy: Math.max(actualEnergy, 0).toExponential(2),
        confidence: Math.min(Math.max((1 - tunnelProb * timeVariance) * 100, 0), 100).toFixed(1)
      },
      spinState: {
        state: spinState,
        probability: Math.min(Math.max(spinProbability, 0), 1).toFixed(3)
      },
      decayProperties: {
        halfLife: Math.max(halfLife, 0).toExponential(2),
        decayConstant: Math.max(decayConstant, 0).toExponential(2)
      },
      tunneling: {
        probability: Math.min(Math.max(tunnelProb, 0), 1).toExponential(3),
        barrierHeight: Math.max(barrierHeight, 0).toExponential(2)
      }
    };
  }

  generateCosmicData(frameCount, frequency) {
    // Enhanced cosmic phenomena with temporal variations
    const timePhase = frameCount * 0.001;
    const quantumNoise = (this.rng.random() - 0.5) * 2;
    const cosmicCycle = Math.sin(timePhase) * 0.5 + 0.5;
    
    // Dynamic probability modulation
    const basePulsarProb = Math.sin(frameCount * 0.01) * 0.5 + 0.5;
    const baseGWProb = Math.cos(frameCount * 0.005) * 0.3 + 0.3;
    
    // Enhanced probability calculations with quantum effects
    const pulsarProb = basePulsarProb * (1 + cosmicCycle * 0.3) * (1 + Math.abs(quantumNoise) * 0.2);
    const gwProb = baseGWProb * (1 + cosmicCycle * 0.4) * (1 + Math.abs(quantumNoise) * 0.3);
    
    // Dynamic CMB temperature with quantum fluctuations
    const cmbBaseTemp = 2.725;
    const cmbVariation = (this.rng.random() * 0.02 - 0.01) * (1 + cosmicCycle * 0.5);
    const cmbTemp = cmbBaseTemp + cmbVariation;
    
    // Enhanced anisotropy calculation
    const anisotropy = (this.rng.random() * 1e-5) * (1 + Math.abs(Math.sin(timePhase * 2)));

    // Pulsar analysis with enhanced characteristics
    const pulsarDetected = this.rng.random() < pulsarProb;
    const pulsarPeriod = frequency > 0 ? (1 / frequency) * (1 + quantumNoise * 0.1) : Infinity;
    const pulsarSignalStrength = pulsarDetected ?
      0.4 + this.rng.random() * 0.6 * (1 + cosmicCycle * 0.3) : 0;

    // Enhanced gravitational wave detection
    const gwDetected = this.rng.random() < gwProb;
    const gwStrain = (this.rng.random() * 1e-21) * (1 + cosmicCycle) * (1 + Math.abs(quantumNoise));

    // Expanded astronomical event types
    const eventTypes = [
      { type: 'Pulsar', baseProb: 0.4, class: ['millisecond', 'binary', 'magnetar'] },
      { type: 'Supernova', baseProb: 0.1, class: ['type-Ia', 'type-II', 'hypernova'] },
      { type: 'Neutron Star', baseProb: 0.2, class: ['isolated', 'binary', 'recycled'] },
      { type: 'Black Hole', baseProb: 0.15, class: ['stellar', 'intermediate', 'supermassive'] },
      { type: 'Gamma Ray Burst', baseProb: 0.05, class: ['short', 'long', 'ultra-long'] },
      { type: 'Fast Radio Burst', baseProb: 0.1, class: ['repeating', 'single', 'periodic'] }
    ];

    // Generate dynamic events based on cosmic conditions
    const events = eventTypes.reduce((acc, eventType) => {
      const eventProb = eventType.baseProb * (1 + cosmicCycle * 0.5) * (1 + Math.abs(quantumNoise) * 0.3);
      if (this.rng.random() < eventProb) {
        const classIndex = Math.floor(this.rng.random() * eventType.class.length);
        acc.push({
          type: eventType.type,
          class: eventType.class[classIndex],
          frequency: frequency * (1 + quantumNoise * 0.2),
          intensity: 0.3 + this.rng.random() * 0.7 * (1 + cosmicCycle * 0.4)
        });
      }
      return acc;
    }, []);

    return {
      pulsarAnalysis: {
        detected: pulsarDetected,
        period: pulsarPeriod.toExponential(2),
        frequency: frequency.toExponential(2),
        signalStrength: pulsarSignalStrength.toFixed(3)
      },
      gravitationalWaves: {
        detected: gwDetected,
        strain: gwStrain.toExponential(2),
        confidence: (0.5 + Math.random() * 0.5 * (1 + cosmicCycle)).toFixed(3)
      },
      backgroundRadiation: {
        temperature: cmbTemp.toFixed(6),
        anisotropy: anisotropy.toExponential(2),
        fluctuation: (Math.abs(quantumNoise) * cosmicCycle).toFixed(3)
      },
      astronomicalEvents: {
        detected: events.length > 0,
        events: events,
        totalIntensity: events.reduce((sum, e) => sum + e.intensity, 0).toFixed(3)
      }
    };
  }

  generateSignalMetrics() {
    // Enhanced temporal dynamics
    const timePhase = this.frameCount * 0.001;
    const quantumCycle = Math.sin(timePhase) * 0.5 + 0.5;
    const quantumNoise = (this.rng.random() - 0.5) * 2;

    // Generate base metrics with enhanced variability
    const baseSnr = 5 + this.rng.random() * 5 * (1 + quantumCycle * 0.5);
    const baseQFactor = 50 + (this.rng.random() * 30 - 15) * (1 + Math.abs(quantumNoise) * 0.3);
    const baseBandwidth = 2.4 + (this.rng.random() * 0.8 - 0.4) * (1 + quantumCycle * 0.2);

    // Enhanced quantum noise effects
    const quantumNoiseBase = Math.sqrt(PLANCK_CONSTANT * baseSnr);
    const quantumNoiseAmplitude = quantumNoiseBase * (this.rng.random() * 2 - 1) *
      (1 + Math.abs(Math.sin(timePhase * 2)) * 0.5);
    
    // Multiple quantum fluctuation components
    const primaryFluctuation = Math.sin(this.getSeededTime() * 0.001) * 0.15;
    const secondaryFluctuation = Math.cos(this.getSeededTime() * 0.002) * 0.1;
    const tertiaryFluctuation = Math.sin(this.getSeededTime() * 0.003 + 1) * 0.05;
    
    const quantumFluctuations = (
      primaryFluctuation +
      secondaryFluctuation * quantumCycle +
      tertiaryFluctuation * Math.abs(quantumNoise)
    );

    // Enhanced signal strength calculation with multiple factors
    const coherenceFactor = 1 + Math.abs(Math.sin(timePhase * 3)) * 0.3;
    const noiseFactor = 1 + Math.abs(quantumNoise) * 0.4;
    const fluctuationFactor = 1 + Math.abs(quantumFluctuations) * 0.5;
    
    const signalStrength = Math.max(0, Math.min(100,
      baseSnr * 10 * coherenceFactor +
      quantumNoiseAmplitude * 20 * noiseFactor +
      quantumFluctuations * 15 * fluctuationFactor
    ));

    // Calculate additional quantum metrics
    const phaseStability = Math.max(0, Math.min(1,
      0.5 + quantumNoise * 0.2 + quantumFluctuations + Math.sin(timePhase) * 0.3
    ));
    
    const coherenceQuality = Math.max(0, Math.min(1,
      0.7 + quantumNoise * 0.15 + Math.cos(timePhase * 2) * 0.15
    ));

    return {
      snr: baseSnr + quantumNoiseAmplitude,
      qFactor: baseQFactor * (1 + quantumFluctuations),
      bandwidth: baseBandwidth * (1 + Math.abs(quantumNoise) * 0.2),
      signalStrength,
      quantumNoise: Math.abs(quantumNoiseAmplitude),
      fluctuationAmplitude: Math.abs(quantumFluctuations),
      phaseStability,
      coherenceQuality,
      quantumCycle,
      temporalPhase: timePhase
    };
  }

  // Enhanced Perlin noise simulation with multiple octaves and dynamic amplitude
  simulatePerlinNoise(t) {
    // Primary wave with varying frequency
    const primary = Math.sin(t) * Math.cos(t * 0.1);
    
    // Secondary waves with phase shifts and frequency modulation
    const secondary = Math.sin(t * 1.5 + 1) * Math.cos(t * 0.15 + 0.5) * 0.8;
    const tertiary = Math.sin(t * 2.1 + 5) * Math.cos(t * 0.2 + 1.5) * 0.6;
    const quaternary = Math.sin(t * 3.2 + 2) * Math.cos(t * 0.25 + 2) * 0.4;
    
    // High-frequency detail with low amplitude
    const detail = (
      Math.sin(t * 4.7 + 3) * 0.2 +
      Math.sin(t * 5.3 + 4) * 0.15 +
      Math.sin(t * 6.1 + 6) * 0.1
    );
    
    // Combine all waves with dynamic scaling
    const timeScale = 1 + Math.sin(t * 0.05) * 0.2; // Slow oscillating scale factor
    const combined = (
      primary +
      secondary * timeScale +
      tertiary * timeScale +
      quaternary * timeScale +
      detail * timeScale
    ) / (2.9 + timeScale); // Normalize to roughly -1 to 1 range
    
    // Convert to 0-1 range with smooth clamping
    return Math.max(0, Math.min(1, combined * 0.5 + 0.5));
  }

  // Increment harvested qubits count and sync with Firebase
  harvestQubit() {
    // Only update local state, let QubitHarvestingSystem handle Firebase updates
    this.currentState.harvestedQubits++;
    this.notifySubscribers();
    return this.currentState.harvestedQubits;
  }

  // Get current harvested qubits count
  getHarvestedQubits() {
    return this.currentState.harvestedQubits;
  }

  // Set harvested qubits count and sync with Firebase
  setHarvestedQubits(count) {
    // Only update local state, let QubitHarvestingSystem handle Firebase updates
    this.currentState.harvestedQubits = count;
    this.notifySubscribers();
  }

  evolveQuantumState() {
    this.frameCount++;
    
    // Get current scanning frequency if scanning
    let frequency;
    if (this.scanningState.isScanning) {
      frequency = this.getScanningFrequency();
    } else {
      // Apply frequency jitter to detected frequency
      const classicalJitter = Math.sin(this.frameCount * 0.1) * 0.001;
      const quantumJitter = (this.rng.random() - 0.5) * 0.0005;
      const totalJitter = classicalJitter + quantumJitter;
      frequency = this.currentState.frequency * (1.0 + totalJitter);
    }

    this.currentState = {
      ...this.generateQuantumMetrics(),
      ...this.generateSignalMetrics(),
      frequency,
      harvestedQubits: this.currentState.harvestedQubits
    };

    this.notifySubscribers();
  }

  // Check for state transitions based on quantum probability
  shouldTransitionState(baseProbability = 0.005) {
    const tunnelFactor = Math.exp(-10 * baseProbability);
    const quantumProbability = baseProbability * (1 + tunnelFactor);
    return this.rng.random() < quantumProbability;
  }

  startSimulation() {
    if (this.simulationInterval) {
      clearInterval(this.simulationInterval);
    }
    this.simulationInterval = setInterval(() => this.evolveQuantumState(), this.updateInterval);
  }

  stopSimulation() {
    if (this.simulationInterval) {
      clearInterval(this.simulationInterval);
      this.simulationInterval = null;
    }
  }

  setUpdateInterval(interval) {
    this.updateInterval = interval;
    if (this.simulationInterval) {
      this.stopSimulation();
      this.startSimulation();
    }
  }

  setConfig(newConfig) {
    this.config = {
      ...this.config,
      ...newConfig
    };
  }

  getCurrentState() {
    return { ...this.currentState };
  }

  subscribe(callback) {
    this.subscribers.add(callback);
    // Immediately send current state to new subscriber
    callback(this.getCurrentState());
    
    // Return unsubscribe function
    return () => {
      this.subscribers.delete(callback);
    };
  }

  notifySubscribers() {
    const state = this.getCurrentState();
    this.subscribers.forEach(callback => callback(state));
  }

  // Advanced quantum state manipulation methods
  induceQuantumFluctuation(intensity = 1.0) {
    const fluctuate = (value, range, intensity) => {
      const maxChange = (range.max - range.min) * 0.2 * intensity;
      const change = (this.rng.random() - 0.5) * 2 * maxChange;
      return Math.max(range.min, Math.min(range.max, value + change));
    };

    // Get current metrics
    const currentMetrics = this.currentState.metrics || {};
    
    // Calculate new metrics with fluctuations
    const newMetrics = {
      stability: fluctuate(currentMetrics.stability || 0.5, this.config.metrics.stability, intensity),
      entropy: fluctuate(currentMetrics.entropy || 0.5, this.config.metrics.entropy, intensity),
      complexity: fluctuate(currentMetrics.complexity || 0.5, this.config.metrics.complexity, intensity),
      coherence: fluctuate(currentMetrics.coherence || 0.5, this.config.metrics.coherence, intensity)
    };

    // Update event probabilities based on new metrics
    const eventProbabilities = {
      anomaly: this.config.eventProbabilities.anomaly * (1 - newMetrics.stability),
      shift: this.config.eventProbabilities.shift * newMetrics.entropy,
      merge: this.config.eventProbabilities.merge * newMetrics.coherence,
      split: this.config.eventProbabilities.split * newMetrics.complexity
    };

    this.currentState = {
      ...this.currentState,
      metrics: newMetrics,
      eventProbabilities,
      harvestedQubits: this.currentState.harvestedQubits
    };

    this.notifySubscribers();
  }

  stabilizeQuantumState() {
    const stabilize = (value, range) => {
      const midpoint = (range.max + range.min) / 2;
      return value + (midpoint - value) * 0.5;
    };

    // Get current metrics
    const currentMetrics = this.currentState.metrics || {};
    
    // Calculate stabilized metrics
    const newMetrics = {
      stability: stabilize(currentMetrics.stability || 0.5, this.config.metrics.stability),
      entropy: stabilize(currentMetrics.entropy || 0.5, this.config.metrics.entropy),
      complexity: stabilize(currentMetrics.complexity || 0.5, this.config.metrics.complexity),
      coherence: stabilize(currentMetrics.coherence || 0.5, this.config.metrics.coherence)
    };

    // Update event probabilities based on stabilized metrics
    const eventProbabilities = {
      anomaly: this.config.eventProbabilities.anomaly * (1 - newMetrics.stability),
      shift: this.config.eventProbabilities.shift * newMetrics.entropy,
      merge: this.config.eventProbabilities.merge * newMetrics.coherence,
      split: this.config.eventProbabilities.split * newMetrics.complexity
    };

    this.currentState = {
      ...this.currentState,
      metrics: newMetrics,
      eventProbabilities,
      harvestedQubits: this.currentState.harvestedQubits
    };

    this.notifySubscribers();
  }
}

// Create and export singleton instance
const quantumEngine = new QuantumEngine();
export default quantumEngine;